import { useState } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useMutation, gql } from '@zenbusiness/application-commons-graphql';
import Button from '@zenbusiness/common-components/Button';
import Text from '@zenbusiness/common-components/Text';
import Title from '@zenbusiness/common-components/Title';
import { Navigate, useParams } from 'react-router-dom';

import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';

import manifest from '#client/manifest';

const { environment } = manifest;

const SETUP_EXTERNAL_DNS_PROCESS = gql`
  mutation SetupExternalDomainDNSProcess($input: SetupExternalDomainDNSProcessInput!) {
    setupExternalDomainDNSProcess(input: $input) {
      domainUuid
      websiteConnectionStatus
    }
  }
`;

const StyledGridRoot = styled(Grid)(({ theme }) => ({
  flexDirection: 'column',
  justifyContent: 'flex-start',
  textAlign: 'center',
  paddingBottom: theme.spacing(12),
  width: 'calc(100vw - 40px)',
  '& *': {
    maxWidth: '100%'
  },
  '& .MuiCheckbox-colorPrimary.Mui-checked': {
    color: theme.palette.primary.main
  },
  '& h2': {
    ...theme.typography.title_huge,
    color: theme.palette.focus90,
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(2.5)
  },
  '& section': {
    border: '2px solid #F1F4F5',
    boxSizing: 'border-box',
    borderRadius: '4px',
    paddingLeft: theme.spacing(4),
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    marginBottom: theme.spacing(2.5),
    backgroundColor: theme.palette.focus10.main
  },
  '& h4, section': {
    textAlign: 'left',
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5)
  },
  '& .MuiIconButton-label': {
    backgroundColor: theme.palette.focus10.main,
    width: '21px',
    height: '21px'
  },
  [theme.breakpoints.up('sm')]: {
    paddingLeft: theme.spacing(2),
    width: '100%',
    '& div': {
      maxWidth: '540px',
      marginLeft: 'auto',
      marginRight: 'auto'
    },
    '& h4': {
      ...theme.typography.title_smallRegular
    }
  },
  [theme.breakpoints.up('xs')]: {
    '& h4': {
      ...theme.typography.title_smallRegular
    }
  }
}));

const StyledButtonGroup = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    position: 'fixed',
    bottom: 0,
    width: '100vw',
    left: 0,
    margin: 0,
    paddingLeft: 0,
    paddingRight: 0,
    justifyContent: 'space-between',
    '& a,button': {
      borderRadius: 0,
      width: '100vw',
      height: '64px',
      opacity: 1
    }
  }
}));

const StyledCheckboxContainer = styled('div')(({ theme }) => ({
  textAlign: 'center',
  paddingTop: theme.spacing(5),
  paddingBottom: theme.spacing(2.5)
}));

const StyledButton = styled(Button)(({ theme }) => ({
  '&, &:hover': {
    textDecoration: 'none'
  },
  '&:disabled': {
    backgroundColor: theme.palette.peace10.main,
    color: theme.palette.focus30.main
  },
  [theme.breakpoints.up('sm')]: {
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(2.5),
    width: '360px',
    textAlign: 'center'
  }
}));

const StyledHelpButton = styled(Button)(({ theme }) => ({
  '&, &:hover': {
    textDecoration: 'none'
  },
  [theme.breakpoints.up('sm')]: {
    marginTop: theme.spacing(2.5),
    marginBottom: theme.spacing(2.5),
    width: '360px',
    textAlign: 'center'
  },
  [theme.breakpoints.down('sm')]: {
    border: 'none',
    backgroundColor: theme.palette.focus10.main,
    '&:hover': {
      backgroundColor: theme.palette.focus10.main,
      textDecoration: 'none'
    }
  }
}));

const ExternalDomainRecords = () => {
  const { businessEntityUuid } = useBusinessEntity();
  const { domainUuid } = useParams();
  const [completed, updateCompleted] = useState(false);
  const [setupExternaDNSProcess, { loading, data }] = useMutation(SETUP_EXTERNAL_DNS_PROCESS);

  const handleChange = (event) => {
    updateCompleted(event.target.checked);
  };

  const handleSubmitClick = useButtonPressedCallback({
    elementName: 'next',
    elementText: 'NEXT',
    pageCategory: 'dashboard',
    pageSection: 'growth',
    screenName: 'external_records'
  });

  const handleSubmit = () => {
    handleSubmitClick();
    setupExternaDNSProcess({
      variables: {
        input: {
          domainUuid
        }
      }
    });
  };

  if (!loading && data) {
    return (<Navigate to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}/external-verify`} />);
  }

  return (
    <StyledGridRoot container direction="column" justifyContent="flex-start">
      <div>
        <Title level={2} variant="lg-alt">
          Add Records
        </Title>
        <Text size="sm">
          To configure your domain registered elsewhere,
          you’ll need to navigate to your domain provider’s DNS page.
          Then, copy the records below into the corresponding
          fields in your domain provider’s DNS settings.
        </Text>
        <Title level={4} variant="md">A Record</Title>
        <section>
          Hostname: @ or domainname.com
          <br />
          TTL: 3600 or 1 hour or default
          <br />
          Data:
          {' '}
          { environment !== 'production' ? '54.158.198.18' : '35.172.94.1'}
        </section>
        <Title level={4} variant="md">CNAME Record</Title>
        <section>
          Hostname: www
          <br />
          TTL: 3600 or 1 hour or default
          <br />
          Points to:
          {' '}
          s.multiscreensite.com
        </section>
        <StyledCheckboxContainer>
          <FormControlLabel
            control={(
              <Checkbox
                color="primary"
                checked={completed}
                onChange={handleChange}
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            )}
            label={(
              <Text size="sm" color="focus40">
                I have completed this step
              </Text>
            )}
          />
        </StyledCheckboxContainer>
      </div>
      <StyledButtonGroup>
        <StyledButton
          size="large"
          variant="contained"
          color="Focus"
          onClick={handleSubmit}
          disabled={!completed}
        >
          NEXT
        </StyledButton>
        <StyledHelpButton
          size="large"
          variant="outlined"
          color="Focus"
          component="a"
          target="_blank"
          rel="noopener"
          href="https://help.zenbusiness.com/en/articles/5184140-connecting-a-domain-purchased-elsewhere"
        >
          VIEW MORE HELP
        </StyledHelpButton>
      </StyledButtonGroup>
    </StyledGridRoot>
  );
};

export default ExternalDomainRecords;
